import * as Headless from '@headlessui/react'
import type React from 'react'
import { forwardRef } from 'react'
import { cn } from '../../shared/cn'
import { Caption } from '../typography/Caption'
import { P2 } from '../typography/Paragraph/P2'
import { P3 } from '../typography/Paragraph/P3'
import { Avatar } from './Avatar'
import { TouchTarget } from './Button'
import { Link } from './Link'

type UserProps = {
  src?: string | null
  fullname: string
  additionalInfo?: string
  small?: boolean
  square?: boolean
  href?: string
}

export function User({ ...props }: UserProps & React.ComponentPropsWithoutRef<'div'>) {
  const { src, fullname, additionalInfo, small, square, href } = props
  const initials =
    fullname && fullname.length > 0
      ? (fullname[0] + (fullname.split(' ')[1]?.[0] ?? '')).toUpperCase()
      : ''

  const content = (
    <>
      <Avatar
        className={cn('bg-algonavy-500 text-white', small ? 'size-6' : 'size-10')}
        src={src}
        initials={initials}
        square={square}
      />
      <div className='flex flex-col'>
        <div className={cn(small ? 'block' : 'hidden')}>
          <P2 className='text-nowrap font-semibold'>{fullname}</P2>{' '}
          <Caption className={cn(additionalInfo ? 'flex text-contentSecondary' : 'hidden')}>
            {additionalInfo}
          </Caption>
        </div>

        <div className={cn(small && 'hidden')}>
          <P3 className='font-semibold'>{fullname}</P3>{' '}
          <Caption className={cn(additionalInfo ? 'flex text-contentSecondary' : 'hidden')}>
            {additionalInfo}
          </Caption>
        </div>
      </div>
    </>
  )

  if (href) {
    return (
      <Link href={href} className='flex flex-row items-center gap-3'>
        {content}
      </Link>
    )
  }

  return <div className='flex flex-row items-center gap-3'>{content}</div>
}

type UserButtonLinkProps = UserProps &
  Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'> & {
    href: string
  }

type UserButtonButtonProps = UserProps & Omit<Headless.ButtonProps, 'as'>

export const UserButton = forwardRef(function UserButton(
  { ...props }: UserButtonLinkProps | UserButtonButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  const { src, fullname, additionalInfo } = props

  if ('href' in props && typeof props.href === 'string') {
    const linkProps = props as UserButtonLinkProps
    return (
      <Link {...linkProps} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
        <TouchTarget>
          <User fullname={fullname} additionalInfo={additionalInfo} src={src}></User>
        </TouchTarget>
      </Link>
    )
  }

  const buttonProps = props as UserButtonButtonProps
  return (
    <Headless.Button {...buttonProps} ref={ref}>
      <TouchTarget>
        <User fullname={fullname} additionalInfo={additionalInfo} src={src}></User>
      </TouchTarget>
    </Headless.Button>
  )
})
